import { UIProperty } from '@api/v1';

export const CONFIDENCE_THRESHOLD = 0.65;

/**
 * German street-type abbreviation pairs [pattern, canonical short form].
 * Both the description and the property name are reduced to the same canonical form
 * so that e.g. "Straße" and "Str." produce the same token.
 */
const STREET_TYPE_ABBREVIATIONS: Array<[RegExp, string]> = [
	[/stra(ß|ss)e/gi, 'str'],
	[/platz/gi, 'pl'],
	[/allee/gi, 'al'],
	[/gasse/gi, 'g']
];

/**
 * Normalise a raw address string into a set of lowercase tokens, applying:
 *   - leading noise-prefix stripping ("72583-WEG ", "300 WEG ", "WEG ", "73-", "512 ")
 *   - street-type abbreviation collapsing
 *   - number-range normalisation ("1,3" / "1 - 3" / "40/41" → "1-3" / "1-3" / "40-41")
 *   - inline letter-suffix collapsing ("2 a" → "2a")
 *   - letter-suffix expansion ("13a+b" → "13a 13b", "13a-b" → "13a 13b")
 */
export function normaliseTokens(input: string): Set<string> {
	let s = input;

	// Strip leading WEG prefix with optional numeric ID: "72583-WEG ", "300 WEG ", "WEG ", "WEG-"
	s = s.replace(/^(\d+[-\s])?WEG[-\s]+/i, '');
	// Strip bare leading numeric prefix (dash or space): "73-", "512 "
	s = s.replace(/^\d+[-\s]+(?=[a-zA-ZäöüÄÖÜß])/, '');

	// Normalise number ranges to a dash with no spaces
	s = s.replace(/(\d)\s*,\s*(\d)/g, '$1-$2'); // "1,3"  → "1-3"
	s = s.replace(/(\d)\s*\/\s*(\d)/g, '$1-$2'); // "40/41" → "40-41"
	s = s.replace(/(\d)\s+-\s+(\d)/g, '$1-$2'); //  "1 - 3" → "1-3"

	// Collapse inline letter suffixes: "2 a" → "2a"
	s = s.replace(/(\d)\s+([a-zA-Z])\b/g, '$1$2');

	// Expand letter-suffix combinations: "13a+b" → "13a 13b", "13a-b" → "13a 13b"
	s = s.replace(/(\d+)([a-zA-Z])\s*[+&]\s*([a-zA-Z])\b/g, '$1$2 $1$3');
	s = s.replace(/(\d+)([a-zA-Z])\s*-\s*([a-zA-Z])\b/g, '$1$2 $1$3');

	// Collapse street-type names to canonical short forms
	for (const [pattern, replacement] of STREET_TYPE_ABBREVIATIONS) {
		s = s.replace(pattern, replacement);
	}

	// Tokenise on word-character boundaries (including German umlauts).
	// Hyphens act as separators, so "Kaiser-Friedrich-Ufer" → ["kaiser","friedrich","ufer"]
	// and "1-3" → ["1","3"] — individual tokens that match their counterparts.
	const tokens = s.toLowerCase().match(/[a-z0-9äöüßÄÖÜ]+/g) ?? [];
	return new Set(tokens);
}

/**
 * Score one description against one property name.
 *
 * Uses an F1-style metric (harmonic mean of precision and recall) so that a
 * short property name like "WEG Orleanstr. 43" can score well against a longer
 * description that contains extra postal/city tokens.
 *
 * Additionally applies a 0.5× penalty when *both* sides contain pure-numeric
 * tokens that do not appear on the other side — this prevents a house-number
 * mismatch (e.g. "Musterstr. 1" vs "Musterstr. 10") from scoring above the
 * threshold when the correct option is absent.
 */
/**
 * Like normaliseTokens but additionally strips the trailing street-type suffix
 * "-weg" from each token. This allows abbreviated compound property labels like
 * "Ahorn-/Akazien-/Kornweg" (→ tokens "ahorn", "akazien", "korn") to align
 * with the spelled-out forms "Ahornweg", "Akazienweg", "Kornweg" that appear
 * in Pantaenius descriptions.
 *
 * Not exported — only used inside scoreMatch to avoid changing the observable
 * behaviour of normaliseTokens.
 */
function normaliseForScoring(input: string): Set<string> {
	const base = normaliseTokens(input);
	const result = new Set<string>();
	for (const t of base) {
		result.add(t.endsWith('weg') && t.length > 3 ? t.slice(0, -3) : t);
	}
	return result;
}

export function scoreMatch(description: string, propertyName: string): number {
	const descTokens = normaliseForScoring(description);
	if (descTokens.size === 0) return 0;

	const propCandidates = propertySegments(propertyName);
	let bestScore = 0;

	for (const propCandidate of propCandidates) {
		const propTokens = normaliseForScoring(propCandidate);
		if (propTokens.size === 0) continue;

		let intersectionSize = 0;
		for (const token of propTokens) {
			if (descTokens.has(token)) intersectionSize++;
		}

		if (intersectionSize === 0) continue;

		const precision = intersectionSize / descTokens.size;
		const recall = intersectionSize / propTokens.size;
		let score = (2 * precision * recall) / (precision + recall);

		// Numeric-mismatch penalty: only apply when BOTH sides have exclusive numerics.
		const isNumeric = (t: string) => /^\d+$/.test(t);
		const descNumerics = new Set([...descTokens].filter(isNumeric));
		const propNumerics = new Set([...propTokens].filter(isNumeric));

		const descHasExclusive = [...descNumerics].some((n) => !propNumerics.has(n));
		const propHasExclusive = [...propNumerics].some((n) => !descNumerics.has(n));

		if (descHasExclusive && propHasExclusive) {
			score *= 0.5;
		}

		// Containment bonus: if every description token appears in the property
		// (precision == 1) and the description is specific enough (≥ 3 tokens), the
		// low recall is simply because the property name is a compound label with
		// extra segments. Ensure we still report a confident match.
		if (precision === 1 && descTokens.size >= 3) {
			score = Math.max(score, 0.8);
		}

		// Symmetric containment bonus: if all property tokens appear in the
		// description (recall == 1) and the property has enough substance (≥ 2
		// tokens with at least one non-numeric), the description is likely a
		// detailed enumeration that fully contains this property's address.
		if (recall === 1 && propTokens.size >= 2 && [...propTokens].some((t) => !isNumeric(t))) {
			score = Math.max(score, 0.8);
		}

		if (score > bestScore) bestScore = score;
	}

	return bestScore;
}

/**
 * Split an address string into candidate segments by "/".
 *
 * Pantaenius descriptions sometimes concatenate multiple addresses with "/"
 * as a separator (e.g. "Bochumer Str. 2-6 / Grendpl. 2a 45276 Essen").
 * We try the full string AND each "/" segment so the best partial match wins.
 * The full string is always included to handle cases where "/" is part of a
 * house-number range like "40/41" — the normaliser handles those correctly.
 */
function addressSegments(input: string): string[] {
	const parts = input
		.split(/\s*\/\s*/)
		.map((s) => s.trim())
		.filter(Boolean);
	if (parts.length <= 1) return [input];
	return [input, ...parts];
}

function descriptionSegments(description: string): string[] {
	return addressSegments(description);
}

function propertySegments(propertyName: string): string[] {
	return addressSegments(propertyName);
}

/**
 * Find the best-matching property for a Pantaenius contract description.
 *
 * Returns the top candidate if its score meets the confidence threshold,
 * otherwise returns null (no pre-selection).
 */
export function findBestPropertyMatch(
	description: string,
	candidates: UIProperty[],
	threshold = CONFIDENCE_THRESHOLD
): UIProperty | null {
	if (!description?.trim() || !candidates.length) return null;

	const segments = descriptionSegments(description);

	let bestScore = -1;
	let bestMatch: UIProperty | null = null;

	for (const property of candidates) {
		const score = Math.max(...segments.map((seg) => scoreMatch(seg, property.name)));
		if (score > bestScore) {
			bestScore = score;
			bestMatch = property;
		}
	}

	return bestScore >= threshold ? bestMatch : null;
}
